/* eslint-disable @next/next/no-img-element */
import { CSSProperties, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  ContentType,
  WeeklyTierListRank,
} from "@dimensional-engineering/dimensional-models";
import { Roboto_Mono } from "next/font/google";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";

import styles from "./WeeklyTierList.module.css";
import {
  TIER_COLORS,
  TIER_ROWS,
  TierPeerPoint,
} from "./WeeklyTierListTiers";
import WeeklyTierListShareView from "./WeeklyTierListShareView";

import { RootState } from "@/redux/store";
import ModalWrapper from "@/components/shared/ModalWrapper/ModalWrapper";
import CloseButtonSummary from "@/components/shared/CloseButtonSummary";
import { useModalContext } from "@/context/ModalContext";
import { DailyStoriesInterractionBar } from "@/components/results/Snippets/SnippetInterractionBar/SnippetInterractionBar";
import { Mixpanel } from "@/helpers/mixpanel";
import { useAuth } from "@/context/AuthContext";
import ShareCaptionModal from "../ABSTRACTS/AppHomeSharedTab/ShareCaptionModal/ShareCaptionModal";
import { SharedPostWithCommentThread } from "@/models/sharedModels";
import SharedPostModal from "../ABSTRACTS/AppHomeSharedTab/SharedPostModal/SharedPostModal";
import SmallProfileImage from "@/components/shared/SmallProfileImage/SmallProfileImage";
import { AnalysisFeedbackSnippets } from "@/components/results/personalityAnalysis/AnalysisFeedback/AnalysisFeedback";
import { db } from "@/_firebase/firebaseConfig";

const font = Roboto_Mono({ subsets: ["latin"] });

type ActiveTooltip = {
  caption: string;
  rank: WeeklyTierListRank;
  publicProfile: RootState["user"]["publicProfileData"];
  profileImage: string;
  sharedPost: SharedPostWithCommentThread;
};

/**
 * Periodic tier list modal opened from the dashboard CTA.
 * Mirrors the WeeklyTwoByTwoModal layout: flex column with a normal-flow
 * title and tier rows; only the close button is absolutely positioned, and
 * the interaction bar pins to the bottom via `margin-top: auto; sticky`.
 *
 * The share-to-IG/Snapchat capture lives in `WeeklyTierListShareView`. The
 * shared-post embed (when a connection posts their result) is handled by
 * the existing `SharedPostModal` flow.
 */
export default function WeeklyTierListModal(props: {
  initialSelectedUserUid?: string;
}) {
  const { setModalOpen, setModalComponent } = useModalContext();
  const { user } = useAuth();

  const { latestTierList, deliveredForLatest } = useSelector(
    (state: RootState) => state.weeklyTierList
  );
  const userPublicProfile = useSelector(
    (state: RootState) => state.user.publicProfileData
  );
  const userImageUrl = useSelector((state: RootState) => state.user.imageURL);
  const sharedPosts = useSelector(
    (state: RootState) => state.sharedPosts
  ).sharedPosts;
  const connectionProfiles = useSelector(
    (state: RootState) => state.connections.publicProfiles
  );

  const [showCaptionModal, setShowCaptionModal] = useState(false);
  const [caption, setCaption] = useState("");
  const [selectedFilterKey, setSelectedFilterKey] = useState<string>("all");
  const [activeTooltip, setActiveTooltip] = useState<ActiveTooltip | null>(
    null
  );
  const [showRating, setShowRating] = useState(false);
  const [feedbackScore, setFeedbackScore] = useState<number | null>(null);
  const tierBoxSize = useResponsiveTierBoxSize();
  const tierRowGap = 20;

  const foundPost = useIsWeeklyTierListShared(user?.uid, latestTierList?.slug);
  const peerPoints = useWeeklyTierListSharedPeerPoints(
    latestTierList?.slug,
    sharedPosts,
    connectionProfiles,
    user?.uid
  );

  const filterUsers = useMemo(() => {
    const users: Array<{ key: string; label: string }> = [];

    if (userPublicProfile?.userName) {
      users.push({
        key: "me",
        label: `@${userPublicProfile.userName}`,
      });
    }

    const seenConnectionKeys = new Set<string>();
    const sortedConnections = [...peerPoints]
      .sort((a, b) => a.userName.localeCompare(b.userName))
      .filter((point) => {
        const key = `connection:${point.authorUid}`;
        if (seenConnectionKeys.has(key)) {
          return false;
        }
        seenConnectionKeys.add(key);
        return true;
      });

    sortedConnections.forEach((point) => {
      users.push({
        key: `connection:${point.authorUid}`,
        label: `@${point.userName}`,
      });
    });

    return users;
  }, [peerPoints, userPublicProfile?.userName]);

  const pointsToRender = useMemo(() => {
    if (selectedFilterKey === "all") {
      return peerPoints;
    }
    if (selectedFilterKey === "me") {
      return [];
    }
    if (selectedFilterKey.startsWith("connection:")) {
      const selectedConnectionUid = selectedFilterKey.replace(
        "connection:",
        ""
      );
      return peerPoints.filter(
        (point) => point.authorUid === selectedConnectionUid
      );
    }
    return peerPoints;
  }, [peerPoints, selectedFilterKey]);

  const showMyPoint = selectedFilterKey === "all" || selectedFilterKey === "me";

  const tierListTitle =
    latestTierList?.listName?.toUpperCase() ?? "WEEKLY TIER LIST";
  const safeImage = userImageUrl || "/default-image.png";

  useEffect(() => {
    if (!props.initialSelectedUserUid) {
      setSelectedFilterKey("all");
      return;
    }
    if (props.initialSelectedUserUid === user?.uid) {
      setSelectedFilterKey("me");
      return;
    }
    setSelectedFilterKey(`connection:${props.initialSelectedUserUid}`);
  }, [props.initialSelectedUserUid, user?.uid]);

  useEffect(() => {
    if (!latestTierList?.slug || !deliveredForLatest) {
      return;
    }
    Mixpanel?.track(
      "Weekly Tier List Viewed",
      {
        slug: latestTierList.slug,
      },
      user?.uid
    );
  }, [deliveredForLatest, latestTierList?.slug, user?.uid]);

  useEffect(() => {
    if (!user?.uid || !latestTierList?.slug) {
      setFeedbackScore(null);
      return;
    }

    const deliveredRef = doc(
      db,
      "members",
      user.uid,
      "weeklyTierLists",
      latestTierList.slug
    );
    const unsubscribe = onSnapshot(deliveredRef, (snapshot) => {
      if (!snapshot.exists()) {
        setFeedbackScore(null);
        return;
      }
      const score = snapshot.data().feedbackScore;
      if (score === null || score === undefined) {
        setFeedbackScore(null);
        return;
      }
      setFeedbackScore(score as number);
      setShowRating(false);
    });

    return () => {
      unsubscribe();
    };
  }, [latestTierList?.slug, user?.uid]);

  if (!latestTierList || !deliveredForLatest) {
    return null;
  }

  function openSharedPostFromTooltip() {
    if (!activeTooltip) {
      return;
    }
    setModalComponent(
      <SharedPostModal
        profileImage={activeTooltip.profileImage}
        publicProfile={activeTooltip.publicProfile}
        sharedPost={activeTooltip.sharedPost}
      />
    );
  }

  function handleMyPointClick() {
    if (!foundPost) {
      return;
    }
    setActiveTooltip({
      caption: foundPost.caption ?? "",
      rank: deliveredForLatest!.rank,
      publicProfile: userPublicProfile,
      profileImage: safeImage,
      sharedPost: foundPost,
    });
  }

  function handlePeerPointClick(point: TierPeerPoint) {
    const peerPost = sharedPosts.find((post) => {
      return (
        post.postContent.contentIdentifier.contentType ===
          ContentType.weeklyTierList &&
        post.postContent.contentIdentifier.contentSlug ===
          latestTierList!.slug &&
        post.authorUid === point.authorUid
      );
    });

    if (!peerPost) {
      return;
    }

    const trimmedCaption = (peerPost.caption ?? "").trim();
    if (!trimmedCaption) {
      return;
    }

    setActiveTooltip({
      caption: trimmedCaption,
      rank: point.rank,
      publicProfile: point.publicProfile,
      profileImage: point.imageUrl,
      sharedPost: peerPost as SharedPostWithCommentThread,
    });
  }

  function handlePost() {
    if (foundPost) {
      Mixpanel?.track(
        "Post viewed",
        {
          source: "from own results modal",
          content_type: "weekly tier list",
          content_type_slug: latestTierList?.slug,
          caption_text: foundPost.caption,
        },
        user?.uid
      );
      return setModalComponent(
        <SharedPostModal
          profileImage={safeImage}
          publicProfile={userPublicProfile}
          sharedPost={foundPost}
        />
      );
    }

    return setShowCaptionModal(true);
  }

  async function handleRating(value: number) {
    if (!user?.uid || !latestTierList?.slug) {
      return;
    }

    await updateDoc(
      doc(db, "members", user.uid, "weeklyTierLists", latestTierList.slug),
      {
        feedbackScore: value,
      }
    );

    setFeedbackScore(value);
    setShowRating(false);

    Mixpanel?.track(
      "Feedback given",
      {
        slug: latestTierList.slug,
        score: value,
        content_type: "weekly_tier_list",
      },
      user?.uid
    );
  }

  return (
    <>
      {showCaptionModal && (
        <ShareCaptionModal
          contentIdentifier={{
            contentSlug: latestTierList.slug,
            contentType: ContentType.weeklyTierList,
          }}
          onClose={(captionText) => {
            setCaption(captionText);
            setShowCaptionModal(false);
          }}
          savedCaption={caption}
        />
      )}
      <ModalWrapper title="" noHeader noLine>
        <main
          className={styles.modalMain}
          onClick={() => setActiveTooltip(null)}
        >
          <button
            type="button"
            className={styles.modalClose}
            onClick={() => setModalOpen(false)}
          >
            <CloseButtonSummary />
          </button>

          <h2 className={`${styles.modalTitle} ${font.className}`}>
            {tierListTitle}
          </h2>

          <div
            className={styles.modalTierList}
            style={
              {
                "--tier-box-size": `${tierBoxSize}px`,
                "--tier-row-gap": `${tierRowGap}px`,
              } as CSSProperties
            }
          >
            {TIER_ROWS.map((rank) => {
              const peersForRank = pointsToRender.filter(
                (p) => p.rank === rank
              );
              const isMyRank = deliveredForLatest.rank === rank;

              return (
                <div key={rank} className={styles.modalTierRow}>
                  <div
                    className={styles.modalTierBlock}
                    style={{ backgroundColor: TIER_COLORS[rank] }}
                  >
                    <span className={styles.modalTierLetter}>{rank}</span>
                  </div>

                  <div className={styles.modalTierAvatars}>
                    {isMyRank && showMyPoint && (
                      <div
                        className={styles.modalTierAvatarPin}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleMyPointClick();
                        }}
                      >
                        <SmallProfileImage
                          noLink
                          medium
                          imageUrl={safeImage}
                          publicProfile={userPublicProfile}
                        />
                      </div>
                    )}
                    {peersForRank.map((point) => (
                      <div
                        key={point.authorUid}
                        className={styles.modalTierAvatarPin}
                        onClick={(event) => {
                          event.stopPropagation();
                          handlePeerPointClick(point);
                        }}
                      >
                        <SmallProfileImage
                          noLink
                          medium
                          imageUrl={point.imageUrl}
                          publicProfile={point.publicProfile}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {activeTooltip && (
              <button
                type="button"
                className={styles.modalCaptionTooltip}
                style={{
                  top: `${tooltipTopForRank(
                    activeTooltip.rank,
                    tierBoxSize,
                    tierRowGap
                  )}px`,
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  openSharedPostFromTooltip();
                }}
              >
                {activeTooltip.caption || "View shared post"}
              </button>
            )}
          </div>

          <div className={styles.modalFilterRow}>
            <span className={styles.modalShowLabel}>Show:</span>
            <button
              type="button"
              className={
                selectedFilterKey === "all"
                  ? styles.modalFilterActive
                  : styles.modalFilter
              }
              onClick={() => setSelectedFilterKey("all")}
            >
              All
            </button>
            {filterUsers.map((filterUser) => (
              <button
                type="button"
                key={filterUser.key}
                className={
                  selectedFilterKey === filterUser.key
                    ? styles.modalFilterActive
                    : styles.modalFilter
                }
                onClick={() => setSelectedFilterKey(filterUser.key)}
              >
                {filterUser.label}
              </button>
            ))}
          </div>

          <div className={styles.modalInteractionBar}>
            {showRating && (
              <div className={styles.modalFeedbackDiv}>
                <AnalysisFeedbackSnippets
                  onAnswer={(value) => {
                    handleRating(value);
                  }}
                  title="How good was this content?"
                  subtitle="Feedback improves your results"
                  currentAnswer={feedbackScore}
                  onClose={() => setShowRating(false)}
                />
              </div>
            )}
            <DailyStoriesInterractionBar
              onRateClick={() => {
                setActiveTooltip(null);
                setShowRating(!showRating);
              }}
              onCommentClick={() => {}}
              onGoDeeper={() => {}}
              onPostClick={() => {
                Mixpanel?.track(
                  "Post to connections CTA pressed",
                  {
                    content_type: "weekly tier list",
                    slug: latestTierList.slug,
                  },
                  user?.uid
                );
                handlePost();
              }}
              posted={!!foundPost}
              commentCount={0}
              onShare={(type) => {
                Mixpanel?.track(
                  "system share modal summoned",
                  {
                    platform: type === "instagram" ? "Instagram" : "Snapchat",
                    content_type: "weekly tier list",
                    content_type_slug: latestTierList.slug,
                  },
                  user?.uid
                );
                setModalComponent(<WeeklyTierListShareView type={type} />);
              }}
            />
          </div>
        </main>
      </ModalWrapper>
    </>
  );
}

// Tooltip floats just above its tier row inside the relative tier list
// container. Row pitch is 70px (50px block + 20px gap).
function tooltipTopForRank(
  rank: WeeklyTierListRank,
  tierBoxSize: number,
  tierRowGap: number
): number {
  const index = TIER_ROWS.indexOf(rank);
  if (index < 0) {
    return 0;
  }
  return Math.max(0, index * (tierBoxSize + tierRowGap) - 36);
}

function useResponsiveTierBoxSize() {
  const [tierBoxSize, setTierBoxSize] = useState(50);

  useEffect(() => {
    const update = () => {
      const responsive = window.innerWidth * 0.145;
      setTierBoxSize(Math.max(50, Math.min(responsive, 64)));
    };

    update();
    window.addEventListener("resize", update);

    return () => {
      window.removeEventListener("resize", update);
    };
  }, []);

  return tierBoxSize;
}

function useIsWeeklyTierListShared(
  userUid: string | undefined,
  tierListSlug: string | undefined
) {
  const sharedPosts = useSelector(
    (state: RootState) => state.sharedPosts
  ).sharedPosts;

  const [foundPost, setFoundPost] =
    useState<SharedPostWithCommentThread | null>(null);

  useEffect(() => {
    const slug = `${userUid}-weeklyTierList-${tierListSlug}`;
    const isFound = sharedPosts.find((post) => post.slug === slug);
    setFoundPost((isFound as SharedPostWithCommentThread) ?? null);
  }, [tierListSlug, sharedPosts, userUid]);

  return foundPost;
}

function useWeeklyTierListSharedPeerPoints(
  tierListSlug: string | undefined,
  sharedPosts: RootState["sharedPosts"]["sharedPosts"],
  connectionProfiles: RootState["connections"]["publicProfiles"],
  userUid: string | undefined
) {
  return useMemo(() => {
    if (!tierListSlug) {
      return [];
    }

    const postsForTierList = sharedPosts.filter((post) => {
      return (
        post.postContent.contentIdentifier.contentType ===
          ContentType.weeklyTierList &&
        post.postContent.contentIdentifier.contentSlug === tierListSlug &&
        post.authorUid !== userUid &&
        post.postContent.weeklyTierListData?.deliveredWeeklyTierList
      );
    });

    return postsForTierList
      .map((post) => {
        const connection = connectionProfiles.find(
          (profile) => profile.ownerUUID === post.authorUid
        );
        const delivered =
          post.postContent.weeklyTierListData?.deliveredWeeklyTierList;

        if (!connection || !delivered) {
          return null;
        }

        return {
          authorUid: post.authorUid,
          rank: delivered.rank,
          imageUrl: connection.imageUrl,
          userName: connection.userName,
          publicProfile: connection,
        } as TierPeerPoint;
      })
      .filter((point): point is TierPeerPoint => !!point);
  }, [tierListSlug, connectionProfiles, sharedPosts, userUid]);
}
